import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MAT_MOMENT_DATE_FORMATS,
} from '@angular/material-moment-adapter';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import {
  MatCalendar,
  MatCalendarCellCssClasses,
} from '@angular/material/datepicker';
import {
  I18NextPipe,
  I18NEXT_SERVICE,
  ITranslationService,
} from 'angular-i18next';
import * as moment from 'moment';
import { Moment } from 'moment';
import { MatMenuTrigger } from '@angular/material/menu';
import { CustomCalendarHeaderComponent } from './custom-calendar-header.component';
import { SingleLetterMomentDateAdapter } from './single-letter-moment-date-adapter';

export type DateRangePreset =
  | 'today'
  | 'yesterday'
  | 'thisWeek'
  | 'lastWeek'
  | 'thisMonth'
  | 'lastMonth'
  | 'thisQuarter'
  | 'custom';

@Component({
    selector: 'app-basic-date-range-picker',
    templateUrl: './basic-date-range-picker.component.html',
    styleUrls: ['./basic-date-range-picker.component.scss'],
    providers: [
        { provide: MAT_DATE_LOCALE, useValue: 'EN-UK' },
        { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
        {
            provide: DateAdapter,
            useClass: SingleLetterMomentDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
        },
        { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
    ],
    standalone: false
})
export class BasicDateRangePickerComponent implements OnInit {
  @Input() showTitle = true;
  @Input() disableDateInputs = true;
  @Input() initialFrom?: Date;
  @Input() initialTo?: Date;

  @Input() minDate?: Date | null = null;

  // NEW: max date support
  @Input() maxDate?: Date | null = null;

  @Input() initialPreset: DateRangePreset = 'thisWeek';

  @Output() rangeChange = new EventEmitter<{ dteFrom: Date; dteTo: Date }>();

  // both calendars, left & right
  @ViewChild('calendarFrom') calendarFrom!: MatCalendar<Moment>;
  @ViewChild('calendarTo') calendarTo!: MatCalendar<Moment>;
  @ViewChild(MatMenuTrigger) menuTrigger!: MatMenuTrigger;

  presets: { key: DateRangePreset; i18nKey: string; defaultLabel: string }[] = [
    { key: 'today',       i18nKey: 'dashboard:Today',        defaultLabel: 'Today' },
    { key: 'yesterday',   i18nKey: 'dashboard:Yesterday',    defaultLabel: 'Yesterday' },
    { key: 'thisWeek',    i18nKey: 'dashboard:This week',    defaultLabel: 'This week' },
    { key: 'lastWeek',    i18nKey: 'dashboard:Last week',    defaultLabel: 'Last week' },
    { key: 'thisMonth',   i18nKey: 'dashboard:This month',   defaultLabel: 'This month' },
    { key: 'lastMonth',   i18nKey: 'dashboard:Last month',   defaultLabel: 'Last month' },
    { key: 'thisQuarter', i18nKey: 'dashboard:This quarter', defaultLabel: 'This quarter' },
  ];

  selectedPreset: DateRangePreset = 'thisWeek';

  fromDate!: Moment;
  toDate!: Moment;
  fromTime = '00:00';
  toTime = '23:59';

  minDateMoment: Moment | null = null;

  // NEW: moment version of maxDate
  maxDateMoment: Moment | null = null;

  // calendar view months: left = current month, right = next month
  leftViewDate!: Moment;
  rightViewDate!: Moment;

  customHeader = CustomCalendarHeaderComponent;

  // internal flag: first click selects fromDate, second click selects toDate
  private isSelectingFrom = true;

  constructor(
    private i18n: I18NextPipe,
    @Inject(I18NEXT_SERVICE) private i18NextService: ITranslationService,
    private adapter: DateAdapter<any>,
    @Inject(MAT_DATE_LOCALE) private locale: string
  ) {}

  ngOnInit(): void {
    this.setupLocale();

    this.minDateMoment = this.minDate ? moment(this.minDate) : null;
this.maxDateMoment = this.maxDate
  ? moment(this.maxDate).utcOffset(0).endOf('day')
  : moment().utcOffset(0).endOf('day');

    if (this.initialFrom && this.initialTo) {
      this.fromDate = moment(this.initialFrom);
      this.toDate = moment(this.initialTo);
      this.selectedPreset = 'custom';
      this.isSelectingFrom = false;
    } else {
      this.selectedPreset = this.initialPreset || 'thisWeek';
      this.applyPreset(this.selectedPreset);
      this.isSelectingFrom = false;
    }

    // clamp initial values into [minDate, maxDate]
    this.fromDate = this.clampToBounds(this.fromDate).startOf('day');
    this.toDate = this.clampToBounds(this.toDate).endOf('day');

    // initial months – "previous month + this month"
    const base = moment(); // current month
    this.rightViewDate = base.clone().startOf('month'); // this month on the RIGHT
    this.leftViewDate = base.clone().subtract(1, 'month').startOf('month'); // previous month on the LEFT

    this.emitRange();
  }

  onMenuClosed(): void {
    // treat closing the menu like pressing Apply
    this.emitRange();
  }

  private setupLocale(): void {
    switch (this.i18NextService.language) {
      case 'NO': this.locale = 'nb'; break;
      case 'DK': this.locale = 'da'; break;
      case 'SE': this.locale = 'sv'; break;
      case 'EN': this.locale = 'en-gb'; break;
      default: this.locale = this.i18NextService.language.toLowerCase();
    }
    this.adapter.setLocale(this.locale);
    moment.locale(this.locale);
  }

  // ---------- NEW: clamp helper ----------
  private clampToBounds(d: Moment): Moment {
    let x = d?.clone?.() ? d.clone() : moment(d);

    if (this.minDateMoment && x.isBefore(this.minDateMoment, 'day')) {
      x = this.minDateMoment.clone();
    }
    if (this.maxDateMoment && x.isAfter(this.maxDateMoment, 'day')) {
      x = this.maxDateMoment.clone();
    }
    return x;
  }

  // ---------- PRESETS ----------

  selectPreset(preset: DateRangePreset): void {
    this.selectedPreset = preset;
    if (preset !== 'custom') {
      this.applyPreset(preset);
      this.isSelectingFrom = false; // both dates are set by preset
      this.refreshCalendars();
    }
  }

  private applyPreset(preset: DateRangePreset): void {
    const now = moment().utcOffset(0);

    switch (preset) {
      case 'today': {
        this.fromDate = now.clone().startOf('day');
        this.toDate = now.clone().endOf('day');
        break;
      }
      case 'yesterday': {
        const d = now.clone().subtract(1, 'day');
        this.fromDate = d.clone().startOf('day');
        this.toDate = d.clone().endOf('day');
        break;
      }
      case 'thisWeek': {
        this.fromDate = now.clone().startOf('week');
        this.toDate = now.clone().endOf('week');
        break;
      }
      case 'lastWeek': {
        const d = now.clone().subtract(1, 'week');
        this.fromDate = d.clone().startOf('week');
        this.toDate = d.clone().endOf('week');
        break;
      }
      case 'thisMonth': {
        this.fromDate = now.clone().startOf('month');
        this.toDate = now.clone().endOf('month');
        break;
      }
      case 'lastMonth': {
        const d = now.clone().subtract(1, 'month');
        this.fromDate = d.clone().startOf('month');
        this.toDate = d.clone().endOf('month');
        break;
      }
      case 'thisQuarter': {
        this.fromDate = now.clone().startOf('quarter');
        this.toDate = now.clone().endOf('quarter');
        break;
      }
      case 'custom':
      default:
        break;
    }

    this.fromTime = '00:00';
    this.toTime = '23:59';

    // clamp preset result into [minDate, maxDate]
    this.fromDate = this.clampToBounds(this.fromDate).startOf('day');
    this.toDate = this.clampToBounds(this.toDate).endOf('day');

    // keep range valid after clamping
    if (this.toDate.isBefore(this.fromDate, 'day')) {
      this.toDate = this.fromDate.clone().endOf('day');
    }

    // adjust calendar view: right = main month (toDate), left = previous month
    if (this.toDate || this.fromDate) {
      const anchor = (this.toDate || this.fromDate).clone().startOf('month');
      this.rightViewDate = anchor; // preset month on the RIGHT
      this.leftViewDate = anchor.clone().subtract(1, 'month'); // previous month on the LEFT
    }
  }

  // ---------- CALENDAR EVENTS (both calendars use this) ----------

  /**
   * Unified click handler for both calendars.
   * First click selects fromDate, second click selects toDate.
   */
  onCalendarDateClick(date: Moment): void {
    this.selectedPreset = 'custom';

    date = this.clampToBounds(date).startOf('day');

    if (!this.fromDate || !this.toDate || !this.isSelectingFrom) {
      // start a new range
      this.fromDate = date.clone().startOf('day');
      this.toDate = date.clone().endOf('day');
      this.isSelectingFrom = true;
    } else {
      // set end date of range
      if (date.isBefore(this.fromDate, 'day')) {
        // clicked before current fromDate -> swap
        this.toDate = this.fromDate.clone().endOf('day');
        this.fromDate = date.clone().startOf('day');
      } else {
        this.toDate = date.clone().endOf('day');
      }
      this.isSelectingFrom = false;
    }

    // clamp again just in case
    this.fromDate = this.clampToBounds(this.fromDate).startOf('day');
    this.toDate = this.clampToBounds(this.toDate).endOf('day');

    if (this.toDate.isBefore(this.fromDate, 'day')) {
      this.toDate = this.fromDate.clone().endOf('day');
    }

    // force both calendars to recompute dateClass
    this.refreshCalendars();
  }

  // ---------- BOTTOM INPUTS (manual typing) ----------

  onFromDateChange(date: Moment): void {
    this.selectedPreset = 'custom';
    this.fromDate = this.clampToBounds(date).startOf('day');

    if (this.toDate && this.toDate.isBefore(this.fromDate, 'day')) {
      this.toDate = this.fromDate.clone().endOf('day');
    }

    this.toDate = this.clampToBounds(this.toDate).endOf('day');

    if (this.fromDate && this.toDate) {
      this.isSelectingFrom = false;
    }

    this.refreshCalendars();
  }

  onToDateChange(date: Moment): void {
    this.selectedPreset = 'custom';
    this.toDate = this.clampToBounds(date).endOf('day');

    if (this.toDate.isBefore(this.fromDate, 'day')) {
      this.toDate = this.fromDate.clone().endOf('day');
    }

    if (this.fromDate && this.toDate) {
      this.isSelectingFrom = false;
    }

    this.refreshCalendars();
  }

  onFromDateInputChange(value: string): void {
    const parsed = moment(value, 'DD.MM.YYYY', true);
    if (parsed.isValid()) {
      this.onFromDateChange(parsed);
    }
  }

  onToDateInputChange(value: string): void {
    const parsed = moment(value, 'DD.MM.YYYY', true);
    if (parsed.isValid()) {
      this.onToDateChange(parsed.endOf('day'));
    }
  }

  onFromTimeChange(value: string): void {
    this.fromTime = value || '00:00';
  }

  onToTimeChange(value: string): void {
    this.toTime = value || '23:59';
  }

  // ---------- HEADER DISPLAY ----------

  get headerPresetLabel(): string {
    if (this.selectedPreset === 'custom') {
      return this.i18n.transform('dashboard:Custom', { defaultValue: 'Custom' });
    }
    const p = this.presets.find((x) => x.key === this.selectedPreset);
    if (!p) {
      return '—';
    }
    return this.i18n.transform(p.i18nKey, { defaultValue: p.defaultLabel });
  }

  get headerRangeLabel(): string {
    const fromText = this.fromDate?.format('DD.MM.YYYY') || '';
    const toText = this.toDate?.format('DD.MM.YYYY') || '';
    return `${fromText} ${this.fromTime} - ${toText} ${this.toTime}`;
  }

  // ---------- RANGE HIGHLIGHTING ----------

  /**
   * Used by mat-calendar [dateClass] to style the selected range
   * (start, end, and middle days).
   */
  dateClass = (cellDate: Moment): MatCalendarCellCssClasses => {
    if (!this.fromDate || !this.toDate) {
      return '';
    }

    const date = cellDate.clone().startOf('day');
    const from = this.fromDate.clone().startOf('day');
    const to = this.toDate.clone().startOf('day');

    if (date.isSame(from, 'day')) {
      return 'drp-range-start';
    }

    if (date.isSame(to, 'day')) {
      return 'drp-range-end';
    }

    if (date.isAfter(from, 'day') && date.isBefore(to, 'day')) {
      return 'drp-range-middle';
    }

    return '';
  };

  // ---------- APPLY / EMIT ----------

  applySelection(): void {
    this.emitRange();

    // close the popover
    if (this.menuTrigger) {
      this.menuTrigger.closeMenu();
    }
  }

  private emitRange(): void {
    if (!this.fromDate || !this.toDate) {
      return;
    }

    const [fromH, fromM] = this.fromTime.split(':').map((x) => +x || 0);
    const [toH, toM] = this.toTime.split(':').map((x) => +x || 0);

    const from = this.clampToBounds(
      this.fromDate.clone().set({
        hour: fromH,
        minute: fromM,
        second: 0,
        millisecond: 0,
      })
    );

    const to = this.clampToBounds(
      this.toDate.clone().set({
        hour: toH,
        minute: toM,
        second: 59,
        millisecond: 0,
      })
    );

    const toDate = to.toDate();
    toDate.setSeconds(toDate.getSeconds() + 1); // keep your old inclusive logic

    this.rangeChange.emit({
      dteFrom: from.toDate(),
      dteTo: toDate,
    });
  }

  // ---------- HELPERS ----------

  private refreshCalendars(): void {
    if (this.calendarFrom) {
      this.calendarFrom.updateTodaysDate();
    }
    if (this.calendarTo) {
      this.calendarTo.updateTodaysDate();
    }
  }

  resetToInitialPreset(): void {
    const preset = this.initialPreset || 'thisWeek';

    this.selectedPreset = preset;
    this.applyPreset(preset);
    this.isSelectingFrom = false; // both dates are set

    this.refreshCalendars();
    this.emitRange();
  }
}
